import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { customValidator } from '../../../../../Utils/validator';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { Subscription } from 'rxjs';
import { FieldValue } from '../../../../../shared/constants/services.enum';

@Component({
  selector: 'app-add-company',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './add-company.component.html',
  styleUrl: './add-company.component.scss'
})
export class AddCompanyComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() showAction = true;
  @Input() disableForm = false;
  @Input() company: any = "";

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() onBtnClick = new EventEmitter<any>();
  lookupKey = LookupKey;
  form: FormGroup;
  private autoSubmitSub?: Subscription;
  private companyCountrySub?: Subscription;
  private lastEmittedSnapshot?: string;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      companyNameEn: ["", [Validators.required, Validators.maxLength(100)]],
      companyNameAr: ["", [customValidator('arabic'), Validators.maxLength(100)]],
      companyEmail: ["", [Validators.required, customValidator('email'), Validators.maxLength(100)]],
      countryCode: [null, [Validators.required]],
      compnayPhone: ["", [Validators.required, customValidator('mobile'), Validators.maxLength(15)]],
      companyCountry: ["", [Validators.required]],
      companyCity: ["", [Validators.maxLength(50),customValidator('safeText')]],
      companyAddress: ["", [Validators.maxLength(50), customValidator('safeText')]],
      companyPoBox: ["", [Validators.maxLength(10), customValidator('safeText')]],
      respondentMemberNumber: [""]
    })

    this.registerCompanyCountryValidation();

  }
  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company'] && changes['company'].currentValue) {
      const company = changes['company'].currentValue;

      this.form.patchValue({
        companyNameEn: company.nameEnglish || '',
        companyNameAr: company.nameArabic || '',
        companyEmail: company.email || '',
        compnayPhone: company.phone || '',
        companyCountry: company.country || null,
        companyCity: company.city || '',
        companyAddress: company.address || '',
        companyPoBox: company.poBox || '',
        countryCode: company.countryCode || null,
        respondentMemberNumber: company.respondentMemberNumber || null
      });
      if (this.disableForm == true) {
        this.form.disable();
      }
      this.updateCompanyCityValidators(this.form.get('companyCountry')?.value);
      this.autoSubmitIfNeeded();
      if (company.respondentMemberNumber) {
        this.form.get("companyNameEn")?.disable();
        this.form.get("companyNameAr")?.disable();
      }
    }

    if (changes['showAction']) {
      if (!this.showAction) {
        this.registerAutoSubmit();
      } else {
        this.teardownAutoSubmit();
      }
    }

  }

  ngOnDestroy(): void {
    this.teardownAutoSubmit();
    this.teardownCompanyCountryValidation();
  }
  // This method is used to submit
  submit() {
    if (this.form.valid) {
      this.emitCompany(true);
    } else {
      this.form.markAllAsTouched();
    }
  }
  // This method is used to close
  close() {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  private registerAutoSubmit(): void {
    if (this.autoSubmitSub) {
      return;
    }
    this.autoSubmitSub = this.form.valueChanges.subscribe(() => {
      this.autoSubmitIfNeeded();
    });
    this.autoSubmitIfNeeded();
  }

  private teardownAutoSubmit(): void {
    if (this.autoSubmitSub) {
      this.autoSubmitSub.unsubscribe();
      this.autoSubmitSub = undefined;
    }
  }

  private registerCompanyCountryValidation(): void {
    const countryControl = this.form.get('companyCountry');
    if (!countryControl) {
      return;
    }

    this.companyCountrySub = countryControl.valueChanges.subscribe((value) => {
      this.updateCompanyCityValidators(value);
    });

    this.updateCompanyCityValidators(countryControl.value);
  }

  private teardownCompanyCountryValidation(): void {
    if (this.companyCountrySub) {
      this.companyCountrySub.unsubscribe();
      this.companyCountrySub = undefined;
    }
  }

  private updateCompanyCityValidators(countryValue: unknown): void {
    const cityControl = this.form.get('companyCity');
    if (!cityControl) {
      return;
    }

    const isUae = this.isUaeCountry(countryValue);
    const validators = isUae
      ? [Validators.required, Validators.maxLength(50), customValidator('safeText')]
      : [Validators.maxLength(50), customValidator('safeText')];

    cityControl.setValidators(validators);
    cityControl.updateValueAndValidity({ emitEvent: false });
  }

  private isUaeCountry(value: unknown): boolean {
    if (!value) {
      return false;
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const candidate =
        (record['Value'] as string | undefined) ??
        (record['value'] as string | undefined) ??
        (record['Name'] as string | undefined) ??
        (record['label'] as string | undefined);
      if (candidate) {
        return this.isUaeCountry(candidate);
      }
    }

    const normalized = String(value).trim().toLowerCase();
    return (
      normalized === FieldValue.UAE.toLowerCase() ||
      normalized === FieldValue.UAE_AR.toLowerCase()
    );
  }

  private autoSubmitIfNeeded(): void {
    if (this.showAction || !this.form.valid) {
      return;
    }
    this.emitCompany(false);
  }

  private emitCompany(closeAfterEmit: boolean): void {
    const formValue = this.form.getRawValue();
    const company = {
      nameEnglish: formValue.companyNameEn,
      nameArabic: formValue.companyNameAr,
      email: formValue.companyEmail,
      phone: formValue.compnayPhone,
      country: formValue.companyCountry,
      city: formValue.companyCity,
      address: formValue.companyAddress,
      poBox: formValue.companyPoBox,
      countryCode: formValue.countryCode,
      respondentMemberNumber: formValue.respondentMemberNumber
    };

    const snapshot = JSON.stringify(company);
    if (!closeAfterEmit && snapshot === this.lastEmittedSnapshot) {
      return;
    }
    this.lastEmittedSnapshot = snapshot;

    this.onBtnClick.emit(company);
    if (closeAfterEmit) {
      this.close();
    }
  }
}
